// ============================================
// TIENDA.JS — Marketplace mobile-first con scroll infinito
// ============================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): el catalogo ya exige sesion.
import { authHeaders } from './utils/storage';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput,
} from './components/helpersClientes';
import {
  ShoppingCart, FolderOpen, Search, Loader2, PartyPopper, X, ArrowLeft,
} from 'lucide-react';
import { tokens } from './styles/tokens';
import SelectorMoneda, { useMoneda } from './components/SelectorMoneda';
import { useIvaModo, ToggleIva, conIvaSegun } from './components/SelectorIva';
import { useClienteTienda } from './components/SelectorCliente';
import CarritoFlotante, { useCarrito, abrirCarrito, volverACotizacion } from './components/CarritoFlotante';
import BarraAgregandoACotizacion from './components/BarraAgregandoACotizacion';
import { useModoAgregar, leerModoAgregar, salirModoAgregar } from './utils/modoAgregarCotizacion';
import { fetchAuth } from './utils/sesion';
import TarjetaProducto from './components/TarjetaProducto';
// DUPLICAR-MIS-PRODUCTOS: se reusa el MISMO modal de alta/edicion de Mis Productos
// (CatalogoAdmin) pre-llenado con los datos Syscom. NO hay modal duplicado.
import { ModalProducto } from './CatalogoAdmin';
import AgregarAListaModal from './components/AgregarAListaModal';
import AgregarAColeccionModal from './components/AgregarAColeccionModal';
import ModalCompartir from './components/ModalCompartir';
import MenuMobile from './components/MenuMobile';
import MegaPanelDesktop from './components/MegaPanelDesktop';
import SidebarFiltros from './components/SidebarFiltros';
// BUSCADOR-PRO (2026-09-21): el desplegable de marcas, categorias y modelos.
import SugerenciasBusqueda from './components/SugerenciasBusqueda';
import { useResponsive } from './hooks/useResponsive';
import { useUsuario } from './context/UsuarioContext';
import { storage, STORAGE_KEYS } from './utils/storage';
import { useNavigate } from 'react-router-dom';

const PER_PAGE = 24;

// INVENTARIO = LA TIENDA (2026-10-19): la Tienda tiene dos caras sobre la MISMA rejilla y los MISMOS filtros
// (rubro, marca, busqueda, CCTV...): 'tienda' (precio y margen) e 'inventario' (existencia, disponible, apartado).
// modo: cara inicial (Almacen > Inventario la abre en 'inventario'); el toggle la cambia en el momento.
export default function Tienda({ modo: modoInicial = null } = {}) {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { puede, accesoTotal, permisosFetched } = useUsuario();
  const veInventario = accesoTotal || (permisosFetched && (puede('almacen.inventario') || puede('almacen.compras')));
  // El enlace a la Hoja de compras solo para quien puede entrar a Compras (menu = gate).
  const veCompras = accesoTotal || (permisosFetched && puede('almacen.compras'));
  const [modo, setModo] = useState(() => {
    if (modoInicial) return modoInicial;
    try { return sessionStorage.getItem('beep_tienda_modo') === 'inventario' ? 'inventario' : 'tienda'; } catch { return 'tienda'; }
  });
  useEffect(() => { try { sessionStorage.setItem('beep_tienda_modo', modo); } catch {} }, [modo]);
  const modoInventario = modo === 'inventario' && veInventario;
  const [soloConExistencia, setSoloConExistencia] = useState(() => { try { return sessionStorage.getItem('beep_tienda_con_existencia') === '1'; } catch { return false; } });
  useEffect(() => { try { sessionStorage.setItem('beep_tienda_con_existencia', soloConExistencia ? '1' : '0'); } catch {} }, [soloConExistencia]);
  // Desde una tarjeta sin inventario: a la Hoja de compras (Almacen > Compras) con el producto buscado.
  const irAHojaCompras = useCallback((producto) => {
    try { sessionStorage.setItem('beep_abrir_hoja_compras', JSON.stringify({ modelo: producto.modelo || producto.nombre || '' })); } catch {}
    // ALMACEN v3: Compras es seccion propia del menu ('compras'); la hoja vive en su pestana Compras.
    storage.set(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB, 'compras');
    navigate('/');
    try { window.dispatchEvent(new CustomEvent('beep-ir-tab', { detail: { tab: 'compras' } })); } catch { /* none */ }
  }, [navigate]);
  // MOTOR DE ORIGEN F3.B: si esta activo el "modo agregar a cotizacion", se muestra la barra
  // llamativa arriba y se OCULTA el carrito flotante (el modo es independiente del carrito).
  const modoAgregar = useModoAgregar();

  // Hidrata filtros desde sessionStorage (preserva selección al volver de PaginaProducto).
  const filtrosGuardados = (() => {
    try {
      const raw = sessionStorage.getItem('beep_tienda_filtros');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [busqueda, setBusqueda] = useState(filtrosGuardados?.busqueda || '');
  const [busquedaDeb, setBusquedaDeb] = useState(filtrosGuardados?.busqueda || '');
  // BUSCADOR-PRO: lo que devuelve el buscador ademas de los productos.
  const [familia, setFamilia] = useState([]);          // variantes del modelo exacto
  const [sugerencia, setSugerencia] = useState(null);  // "¿quisiste decir...?"
  const [conTolerancia, setConTolerancia] = useState(false);
  const [marcaEntendida, setMarcaEntendida] = useState(null);
  const [totalExacto, setTotalExacto] = useState(null);
  const [verSugerencias, setVerSugerencias] = useState(false);
  const [categoria, setCategoria] = useState(filtrosGuardados?.categoria || null);
  const [marca, setMarca] = useState(filtrosGuardados?.marca || '');
  const [fuente, setFuente] = useState(filtrosGuardados?.fuente || 'todas');

  const [productos, setProductos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [paginas, setPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [desglose, setDesglose] = useState({ beep: 0, syscom: 0 });
  const [cargando, setCargando] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);

  const [menuAbierto, setMenuAbierto] = useState(false);
  const [marcasFiltradas, setMarcasFiltradas] = useState(
    Array.isArray(filtrosGuardados?.marcasFiltradas) ? filtrosGuardados.marcasFiltradas : []
  );
  // CCTV FASE 2: filtros por atributos tecnicos + facetas disponibles.
  const [cctvCamaraMp, setCctvCamaraMp] = useState([]);     // [4, 8, ...]
  const [cctvCanales, setCctvCanales] = useState([]);       // [8, 16, ...]
  const [cctvMpMin, setCctvMpMin] = useState(null);         // "soporta hasta" (>=)
  const [facetas, setFacetas] = useState(null);
  const [orden, setOrden] = useState(() => {
    try {
      const g = localStorage.getItem('beep_orden_tienda');
      return ['precio_asc', 'precio_desc', 'stock_desc'].includes(g) ? g : 'precio_asc';
    } catch { return 'precio_asc'; }
  });
  useEffect(() => {
    try { localStorage.setItem('beep_orden_tienda', orden); } catch {}
  }, [orden]);
  const [toast, setToast] = useState(null);
  const [modalLista, setModalLista] = useState(null);
  const [modalColeccion, setModalColeccion] = useState(null);
  const [modalCompartir, setModalCompartir] = useState(null);
  // DUPLICAR-MIS-PRODUCTOS: { producto (prefill o existente), idExistente | null }
  const [modalDuplicar, setModalDuplicar] = useState(null);
  const [guardandoDuplicar, setGuardandoDuplicar] = useState(false);

  const [cliente] = useClienteTienda();
  const { agregarItem, carrito } = useCarrito();
  const { formatear } = useMoneda();
  // Switch compartido Con IVA / Sin IVA (solo afecta lo MOSTRADO).
  const [ivaModo, setIvaModo] = useIvaModo();

  // Totales para el icono de carrito en el header
  const totalItemsCarrito = Number(carrito?.total_items || 0);
  const subtotalCarrito = Number(carrito?.subtotal_mxn || 0);

  // ROUND-TRIP: si el carrito esta "agregando a" una cotizacion, mostramos un
  // aviso fijo arriba con un boton para volver al editor de esa cotizacion.
  const agregandoA = carrito?.cotizacion_destino_id
    ? { id: carrito.cotizacion_destino_id, etiqueta: carrito.cotizacion_destino_folio || carrito.cotizacion_destino_nombre || 'la cotizacion' }
    : null;

  const sentinelRef = useRef(null);
  const debTimerRef = useRef(null);

  // Debounce buscador
  useEffect(() => {
    if (debTimerRef.current) clearTimeout(debTimerRef.current);
    debTimerRef.current = setTimeout(() => setBusquedaDeb(busqueda), 300);
    return () => clearTimeout(debTimerRef.current);
  }, [busqueda]);

  // Reset al cambiar filtros principales
  useEffect(() => {
    setPagina(1);
    setProductos([]);
  }, [busquedaDeb, categoria, marca, fuente, cliente?.id, marcasFiltradas, orden, cctvCamaraMp, cctvCanales, cctvMpMin, modoInventario, soloConExistencia]);

  // Persiste filtros en sessionStorage para preservarlos al volver desde PaginaProducto.
  useEffect(() => {
    try {
      sessionStorage.setItem('beep_tienda_filtros', JSON.stringify({
        busqueda: busquedaDeb,
        categoria,
        marca,
        fuente,
        marcasFiltradas,
      }));
    } catch {}
  }, [busquedaDeb, categoria, marca, fuente, marcasFiltradas]);

  // Reset de marcas cuando cambia la categoría (las marcas pertenecen al árbol filtrado).
  // Skip en el primer render para no clobberar marcasFiltradas hidratadas desde sessionStorage.
  const primerRenderCategoria = useRef(true);
  useEffect(() => {
    if (primerRenderCategoria.current) {
      primerRenderCategoria.current = false;
      return;
    }
    setMarcasFiltradas([]);
    // CCTV FASE 2: los atributos tecnicos pertenecen a la categoria mostrada.
    setCctvCamaraMp([]);
    setCctvCanales([]);
    setCctvMpMin(null);
  }, [categoria]);

  // Carga de página actual (acumula para scroll infinito)
  const cargarPagina = useCallback(async (pag) => {
    if (pag === 1) setCargando(true);
    else setCargandoMas(true);
    try {
      const qs = new URLSearchParams();
      if (busquedaDeb.trim()) qs.set('q', busquedaDeb.trim());
      if (categoria) qs.set('categoria', categoria);
      if (marca) qs.set('marca', marca);
      if (marcasFiltradas.length > 0) qs.set('marcas', marcasFiltradas.join(','));
      if (fuente && fuente !== 'todas') qs.set('fuente', fuente);
      if (cliente?.id) qs.set('cliente_id', String(cliente.id));
      qs.set('orden', orden);
      // INVENTARIO = LA TIENDA: la cara del almacen y el filtro "solo con existencia".
      if (modoInventario) { qs.set('modo', 'inventario'); if (soloConExistencia) qs.set('con_existencia', '1'); }
      // CCTV FASE 2: filtros tecnicos
      if (cctvCamaraMp.length > 0) qs.set('camara_mp', cctvCamaraMp.join(','));
      if (cctvCanales.length > 0) qs.set('grabador_canales', cctvCanales.join(','));
      if (cctvMpMin) qs.set('grabador_mp_min', String(cctvMpMin));
      qs.set('pagina', String(pag));
      qs.set('limit', String(PER_PAGE));
      const r = await fetch(`${API_BASE_CLI}/api/tienda/buscar?${qs.toString()}`, { headers: authHeaders() }).then(x => x.json());
      if (r.ok) {
        setFacetas(r.facetas || null);
        if (pag === 1) {
          setProductos(r.productos || []);
        } else {
          // Evita duplicados por id_unico al concatenar
          const yaTengo = new Set();
          setProductos(prev => {
            for (const p of prev) yaTengo.add(p.id_unico);
            const nuevos = (r.productos || []).filter(p => !yaTengo.has(p.id_unico));
            return [...prev, ...nuevos];
          });
        }
        setTotal(r.total || 0);
        setPaginas(r.paginas || 1);
        setDesglose(r.desglose || { beep: 0, syscom: 0 });
        // BUSCADOR-PRO: solo en la primera pagina; al hacer scroll infinito no se re-pintan.
        if (pag === 1) {
          setFamilia(r.familia || []);
          setSugerencia(r.sugerencia || null);
          setConTolerancia(!!r.con_tolerancia);
          setMarcaEntendida(r.marca_entendida || null);
          setTotalExacto(r.total_exacto != null ? r.total_exacto : null);
        }
      }
    } finally {
      setCargando(false);
      setCargandoMas(false);
    }
  }, [busquedaDeb, categoria, marca, fuente, cliente, marcasFiltradas, orden, cctvCamaraMp, cctvCanales, cctvMpMin, modoInventario, soloConExistencia]);

  useEffect(() => { cargarPagina(pagina); }, [cargarPagina, pagina]);

  // Scroll infinito vía IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && !cargando && !cargandoMas && pagina < paginas) {
        setPagina(p => p + 1);
      }
    }, { rootMargin: '300px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [cargando, cargandoMas, pagina, paginas]);

  const mostrarToast = useCallback((m, duracion = 2200) => {
    setToast(m);
    setTimeout(() => setToast(null), duracion);
  }, []);

  const handleAgregarCarrito = useCallback(async (producto, cantidad = 1) => {
    // F3.C: en "modo agregar a cotizacion" el producto va DIRECTO a la cotizacion (sin
    // carrito), via el endpoint de F3.A. La Tienda es catalogo normal -> precio de HOY (sin
    // precio forzado). producto.referencia_id ya trae la variante elegida ("base:var").
    const modo = leerModoAgregar();
    if (modo) {
      // Mejora #4b: si el modo es SUSTITUIR -> reemplaza la linea (PUT .../sustituir), sale del
      // modo y vuelve al cotizador. Si no, append normal (intacto).
      if (modo.sustituirLineaId != null) {
        try {
          const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos/${modo.sustituirLineaId}/sustituir`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cliente_id: cliente?.id || null, tipo: producto.tipo, referencia_id: producto.referencia_id }),
          });
          const d = await resp.json().catch(() => ({}));
          if (!resp.ok || !d.ok) { mostrarToast('⚠️ ' + (d.error || 'No se pudo sustituir')); return; }
          mostrarToast('✅ Producto sustituido');
          salirModoAgregar();
          try { window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: modo.cotizacionId } })); } catch { /* none */ }
        } catch (e) {
          if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo sustituir'));
        }
        return;
      }
      try {
        const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cliente_id: cliente?.id || null,
            items: [{ tipo: producto.tipo, referencia_id: producto.referencia_id, cantidad }],
          }),
        });
        const d = await resp.json().catch(() => ({}));
        if (!resp.ok || !d.ok) { mostrarToast('⚠️ ' + (d.error || 'No se pudo agregar a la cotizacion')); return; }
        mostrarToast(`✅ Agregado a la cotizacion: ` + producto.nombre.slice(0, 32));
        // F3.F: avisa a la barra para refrescar el contador "Llevas N" al instante.
        try { window.dispatchEvent(new CustomEvent('beep-agregado-a-cotizacion')); } catch { /* none */ }
      } catch (e) {
        if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo agregar'));
      }
      return; // NO sale del modo: se pueden agregar varios seguidos hasta pulsar "Listo".
    }
    // Sin modo: carrito normal (INTACTO).
    try {
      await agregarItem(producto, cantidad);
      mostrarToast(`✅ Agregado ${cantidad > 1 ? `(${cantidad}) ` : ''}` + producto.nombre.slice(0, 32));
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    }
  }, [cliente, agregarItem, mostrarToast]);

  const handleAgregarLista = useCallback((producto) => {
    setModalLista({
      nombre_display: producto.nombre,
      tipo: producto.tipo,
      referencia_id: producto.referencia_id,
      precio_base_mxn: producto.costo_mxn || producto.precio_efectivo_mxn,
    });
  }, []);

  const handleAgregarColeccion = useCallback((producto) => {
    setModalColeccion({
      tipo: producto.tipo,
      referencia_id: producto.referencia_id,
      nombre: producto.nombre,
      marca: producto.marca,
      modelo: producto.modelo,
      imagen_url: producto.imagen_url,
    });
  }, []);

  const handleCompartir = useCallback((producto) => {
    setModalCompartir(producto);
  }, []);

  // REFRESCO-SYSCOM: el vendedor ve "Sin stock" y duda -> aprieta Actualizar y
  // consultamos a Syscom SOLO ese producto. Si cambio algo, se reemplaza la
  // tarjeta en la lista al instante (sin recargar la busqueda, que perderia el
  // scroll) y el toast dice QUE cambio. Si Syscom falla, el producto se queda
  // como estaba y el aviso explica por que.
  const handleRefrescarSyscom = useCallback(async (producto) => {
    try {
      const qs = cliente?.id ? `?cliente_id=${encodeURIComponent(cliente.id)}` : '';
      const resp = await fetchAuth(
        `${API_BASE_CLI}/api/tienda/producto/${encodeURIComponent(producto.id_unico)}/refrescar${qs}`,
        { method: 'POST' }
      );
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) {
        mostrarToast('⚠️ ' + (d.error || 'No se pudo actualizar desde Syscom'), 6000);
        return;
      }
      if (d.producto) {
        setProductos(prev => prev.map(p => (p.id_unico === d.producto.id_unico ? d.producto : p)));
      }
      if (d.cambios && d.cambios.length > 0) {
        mostrarToast('✅ Actualizado: ' + d.resumen, 5000);
      } else {
        mostrarToast('✅ ' + (d.resumen || 'Ya estaba al dia'), 3000);
      }
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') {
        mostrarToast('⚠️ ' + (e.message || 'No se pudo actualizar desde Syscom'), 5000);
      }
    }
  }, [cliente, mostrarToast]);

  // DUPLICAR-MIS-PRODUCTOS: 3 clics. 1) boton en la tarjeta Syscom -> 2) modal de
  // Mis Productos PRE-llenado (Ricardo captura SU inventario y ajusta precio) ->
  // 3) guardar. Si ya existe uno con ese SKU/modelo, avisa y ofrece editarlo.
  const handleDuplicarMisProductos = useCallback(async (producto) => {
    const sku = (producto.modelo || '').trim();
    try {
      if (sku) {
        const r = await fetch(`${API_BASE_CLI}/api/catalogo/productos/existe?sku=${encodeURIComponent(sku)}`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok && r.data) {
          const pestanaEx = r.data.tipo === 'cable' ? 'Cableado'
            : r.data.tipo === 'servicio' ? 'Servicios e Instalaciones' : 'Productos';
          const editar = window.confirm(
            `Ya lo tienes en Mis Productos (pestaña ${pestanaEx}): "${r.data.nombre}"` +
            `${r.data.stock != null ? ` — inventario actual: ${r.data.stock}` : ''}.\n\n` +
            'Aceptar = editar ese producto (precio / inventario). Cancelar = no hacer nada.'
          );
          if (editar) setModalDuplicar({ producto: r.data, idExistente: r.data.id });
          return;
        }
      }
      // Prefill desde Syscom: costo de arranque = costo Syscom actual; el precio de
      // venta arranca en el efectivo actual y Ricardo lo ajusta en la calculadora.
      // FIX-DUPLICAR: el TIPO se deriva de la categoria/nombre (cable/alambre/bobina ->
      // 'cable') para que el duplicado caiga en la pestana donde Ricardo lo busca
      // (Cableado). Editable en el modal si la heuristica falla.
      const path = Array.isArray(producto.categoria_path) ? producto.categoria_path : [];
      const textoTipo = `${path.join(' ')} ${producto.nombre || ''}`.toLowerCase();
      const esCable = /cable|alambre|bobina|utp|coaxial/.test(textoTipo);
      setModalDuplicar({
        producto: {
          nombre: producto.nombre || '',
          descripcion: producto.descripcion || '',
          sku,
          proveedor: producto.marca || 'Syscom',
          categoria: path.length ? path[path.length - 1] : 'Otros',
          imagen_url: producto.imagen_url || null,
          costo_interno_mxn: Number(producto.costo_mxn) || 0,
          precio_venta_mxn: Number(producto.precio_efectivo_mxn) || 0,
          tipo: esCable ? 'cable' : 'producto',
          unidad: 'pieza',
          moneda_base: 'MXN',
        },
        idExistente: null,
      });
    } catch (e) {
      mostrarToast('⚠️ ' + (e.message || 'No se pudo abrir el duplicado'));
    }
  }, [mostrarToast]);

  const guardarDuplicado = useCallback(async (payload, id) => {
    setGuardandoDuplicar(true);
    try {
      const url = id
        ? `${API_BASE_CLI}/api/catalogo/productos/${id}`
        : `${API_BASE_CLI}/api/catalogo/productos`;
      const resp = await fetchAuth(url, {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { mostrarToast('⚠️ No se pudo guardar: ' + (d.error || 'error del servidor'), 5000); return; }
      setModalDuplicar(null);
      // Aviso claro y durable: QUE paso y DONDE quedo (pestana de Mis Productos).
      const pestana = payload.tipo === 'cable' ? 'Cableado'
        : payload.tipo === 'servicio' ? 'Servicios e Instalaciones' : 'Productos';
      mostrarToast(`✅ ${id ? 'Actualizado' : 'Creado'} en Mis Productos → pestaña ${pestana}. Ya puedes agregarlo a la cotización`, 5000);
      // Refresca la lista: el producto ya aparece como fuente Beep con su inventario.
      cargarPagina(1);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') mostrarToast('⚠️ ' + (e.message || 'No se pudo guardar'));
    } finally {
      setGuardandoDuplicar(false);
    }
  }, [mostrarToast, cargarPagina]);

  const handleSeleccionarCategoria = useCallback((id) => {
    setCategoria(id);
    setMenuAbierto(false);
  }, []);

  // TIENDA-TARJETAS: grid fijo. En movil 2 columnas; en escritorio EXACTAMENTE 5 por fila.
  // minmax(0,1fr) evita desbordes y deja columnas de ancho uniforme.
  const gridStyle = useMemo(() => ({
    display: 'grid',
    gap: isMobile ? 10 : 14,
    padding: isMobile ? 10 : 16,
    gridTemplateColumns: isMobile
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(5, minmax(0, 1fr))',
  }), [isMobile]);

  const hayMas = pagina < paginas;

  // C2-DEFINITIVO (Opcion B): el contenedor de scroll de Dashboard ya NO tiene
  // padding (se movio a un wrapper interior). El header sticky se ancla al borde
  // real del scrollport, asi que ya no hace falta el marginTop negativo ni los
  // margenes negativos del header. El padding visual lo provee el wrapper interior.

  return (
    <div style={{
      fontFamily: PP, color: COLOR_GRIS, background: COLOR_FONDO, minHeight: '100%',
      display: 'flex', flexDirection: 'column', position: 'relative',
    }}>
      {/* F3.B: barra "Agregando a la cotizacion X" (modo independiente del carrito).
          FIX: se OCULTA mientras el mega-menu de categorias esta abierto, para que el menu
          se vea completo (el modo NO se pierde, sigue en storage; al cerrar el menu vuelve). */}
      {!menuAbierto && <BarraAgregandoACotizacion />}
      {/* ROUND-TRIP viejo (flujo carrito NUEVO): aviso "Agregando a la cotizacion X" +
          boton Volver. Se oculta si el nuevo modo esta activo (no duplicar barras). */}
      {!modoAgregar && agregandoA && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          padding: isMobile ? '10px 12px' : '10px 16px',
          background: tokens.primarySoft, borderBottom: `1px solid ${tokens.primary}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, color: COLOR_OSCURO }}>
            <ShoppingCart size={16} strokeWidth={2} color={tokens.primary} />
            <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Agregando a la cotización {agregandoA.etiqueta}
            </span>
          </div>
          <button
            onClick={() => volverACotizacion(carrito.id, agregandoA.id)}
            style={{
              flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: COLOR_OSCURO, color: '#fff', border: 'none', cursor: 'pointer',
              padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: PP, minHeight: 40,
            }}
          ><ArrowLeft size={15} strokeWidth={2} /> Volver a la cotización</button>
        </div>
      )}
      {/* HEADER (NO sticky) — 2 lineas compactas (buscador + filtros). Fluye con el
          contenido y se va hacia arriba al bajar, para ver productos a pantalla completa.
          Al no ser sticky no necesita spacer/padding-top: ocupa su espacio normal en el flujo. */}
      <div style={{
        position: 'static', background: '#fff',
        borderBottom: `1px solid ${COLOR_BORDE}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* LINEA 1: hamburguesa + buscador + toggle MXN/USD */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          flexWrap: 'wrap', rowGap: 8,
          padding: isMobile ? '10px 12px' : '10px 16px',
        }}>
          <button
            onClick={() => setMenuAbierto(true)}
            aria-label="Categorías"
            style={{
              background: 'transparent', border: `1px solid ${COLOR_BORDE}`,
              borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
              fontSize: 18, lineHeight: 1, color: COLOR_OSCURO,
              minWidth: 44, minHeight: 44,
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >☰</button>
          <div style={{ position: 'relative', flex: 1, minWidth: isMobile ? 0 : 220 }}>
            <Search
              size={18}
              strokeWidth={2}
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: COLOR_GRIS_CLARO, pointerEvents: 'none',
              }}
            />
            <input
              type="search"
              placeholder="Buscar productos, modelos, marcas…"
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setVerSugerencias(true); }}
              onFocus={() => setVerSugerencias(true)}
              onBlur={() => setTimeout(() => setVerSugerencias(false), 120)}
              style={{
                ...estiloInput,
                width: '100%', minWidth: 0,
                fontSize: 16, padding: isMobile ? '10px 14px 10px 38px' : '8px 14px 8px 38px',
                fontWeight: 500, minHeight: 44,
              }}
            />
            {/* BUSCADOR-PRO: marcas, categorias y modelos mientras se escribe. */}
            <SugerenciasBusqueda
              texto={busqueda}
              visible={verSugerencias}
              esCelular={isMobile}
              onCerrar={() => setVerSugerencias(false)}
              onElegir={(txt) => { setBusqueda(txt); setVerSugerencias(false); }}
            />
          </div>
          {/* Desktop: SelectorMoneda va en la fila 1. En movil se mueve a su propia fila (abajo)
              para no robarle ancho al buscador. */}
          {!isMobile && (
            <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto={isMobile} />
              <SelectorMoneda compacto={isMobile} />
            </div>
          )}

          {/* Icono carrito — abre el panel deslizante de CarritoFlotante.
              F3.F: OCULTO en "modo agregar a cotizacion" (el contador de la cotizacion
              vive en la barra ambar). Fuera de modo, EXACTO como hoy. */}
          {!modoAgregar && (
          <button
            onClick={abrirCarrito}
            title="Abrir carrito"
            aria-label="Abrir carrito"
            style={{
              background: COLOR_OSCURO, color: '#fff', border: 'none',
              padding: isMobile ? '8px 12px' : '8px 16px',
              borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: PP, flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 8,
              whiteSpace: 'nowrap', minHeight: 44,
              boxShadow: '0 2px 4px rgba(24,95,165,0.2)',
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >
            <ShoppingCart size={16} strokeWidth={2} />
            <span>
              {isMobile
                ? (totalItemsCarrito > 0 ? totalItemsCarrito : '')
                : (totalItemsCarrito > 0
                    ? `${totalItemsCarrito} · ${formatear(conIvaSegun(ivaModo, subtotalCarrito), 'MXN')}`
                    : 'Carrito')}
            </span>
          </button>
          )}
        </div>

        {/* FILA MONEDA (solo movil): SelectorMoneda en su propia fila, alineado a la izquierda,
            para que el buscador de la fila 1 domine y se lea completo. */}
        {isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap',
            padding: '0 12px 10px',
          }}>
            <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto={isMobile} />
            <SelectorMoneda compacto={isMobile} />
          </div>
        )}

        {/* LINEA 2: filtro de catalogo (cliente/expediente se movieron al previsualizador) */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          rowGap: 8,
          padding: isMobile ? '0 12px 10px' : '0 16px 10px',
          flexWrap: 'wrap',
        }}>
          <select
            value={fuente}
            onChange={(e) => setFuente(e.target.value)}
            style={{ ...estiloInput, width: 'auto', fontSize: 13, minHeight: isMobile ? 40 : 36 }}
          >
            <option value="todas">📁 Todo el catálogo</option>
            <option value="beep">📦 Solo servicios Beep</option>
            <option value="syscom">🔌 Solo productos externos</option>
          </select>
          {veInventario && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
              <div role="group" aria-label="Tienda o inventario" style={{ display: 'inline-flex', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, overflow: 'hidden' }}>
                {[['tienda', '🛍️ La Tienda'], ['inventario', '📦 Inventario']].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setModo(id)} aria-pressed={modo === id} style={{ padding: '0 12px', minHeight: isMobile ? 40 : 36, border: 'none', cursor: 'pointer', fontFamily: PP, fontSize: 13, fontWeight: 700, background: modo === id ? COLOR_OSCURO : '#fff', color: modo === id ? '#fff' : COLOR_OSCURO }}>{label}</button>
                ))}
              </div>
              {modoInventario && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: COLOR_GRIS, minHeight: 36, cursor: 'pointer' }}>
                  <input type="checkbox" checked={soloConExistencia} onChange={(e) => setSoloConExistencia(e.target.checked)} /> Solo con existencia
                </label>
              )}
            </div>
          )}
        </div>
      </div>

        {/* Layout cuerpo: sidebar contextual (desktop) + grid principal */}
        <div style={{
          flex: 1, minWidth: 0,
          display: 'flex', flexDirection: 'row',
        }}>
          {!isMobile && categoria && (
            <SidebarFiltros
              categoriaActiva={categoria}
              marcasSeleccionadas={marcasFiltradas}
              onCambiarMarcas={setMarcasFiltradas}
              onCambiarCategoria={(nuevaId) => handleSeleccionarCategoria(nuevaId)}
              onLimpiar={() => {
                handleSeleccionarCategoria(null);
                setMarcasFiltradas([]);
                setCctvCamaraMp([]);
                setCctvCanales([]);
                setCctvMpMin(null);
              }}
              orden={orden}
              onCambiarOrden={setOrden}
              facetas={facetas}
              cctvCamaraMp={cctvCamaraMp}
              onCambiarCamaraMp={setCctvCamaraMp}
              cctvCanales={cctvCanales}
              onCambiarCanales={setCctvCanales}
              cctvMpMin={cctvMpMin}
              onCambiarMpMin={setCctvMpMin}
            />
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Resumen */}
        <div style={{
          padding: isMobile ? '12px 12px 4px' : '14px 20px 4px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          fontSize: 13, color: COLOR_GRIS,
        }}>
          <div>
            {cargando && productos.length === 0
              ? 'Cargando…'
              : <strong>{total}</strong>}
            {!cargando && total > 0 && ` producto${total === 1 ? '' : 's'}`}
            {modoInventario && !cargando && <span style={{ color: COLOR_GRIS_CLARO }}> · cara de inventario{soloConExistencia ? ' · solo con existencia' : ''}</span>}
          </div>
          {categoria && (
            <button
              onClick={() => handleSeleccionarCategoria(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 99, background: tokens.primarySoft,
                color: COLOR_OSCURO, fontWeight: 600, fontSize: 12,
                border: 'none', cursor: 'pointer',
              }}
              title="Limpiar filtro"
            >
              <FolderOpen size={12} strokeWidth={2} /> Categoría seleccionada
              <X size={12} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Barra de orden — mobile siempre, escritorio sólo cuando no hay sidebar */}
        {(isMobile || !categoria) && (
          <BarraOrden
            isMobile={isMobile}
            total={total}
            orden={orden}
            onCambiar={setOrden}
          />
        )}

        {/* Sin resultados */}
        {!cargando && productos.length === 0 && (
          <div style={{
            margin: 16, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO,
            background: '#fff', borderRadius: 12, border: `1px solid ${COLOR_BORDE}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <Search size={36} strokeWidth={1.5} aria-label="Sin resultados" />
            </div>
            <div>Sin resultados. Prueba otra búsqueda o categoría.</div>
            {/* BUSCADOR-PRO: nunca un cero seco. Si hay algo parecido, se ofrece con un toque. */}
            {sugerencia && (
              <div style={{ marginTop: 14, fontSize: 14, color: COLOR_GRIS }}>
                ¿Quisiste decir{' '}
                <button
                  onClick={() => setBusqueda(sugerencia.texto)}
                  style={{
                    background: 'transparent', border: 'none', padding: '6px 8px', minHeight: 44,
                    color: COLOR_PRINCIPAL, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    fontFamily: 'inherit', textDecoration: 'underline',
                  }}
                >{sugerencia.texto}</button>
                ?
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                  {sugerencia.tipo === 'marca' ? 'Es una marca del catálogo' : `Es un modelo${sugerencia.marca ? ' de ' + sugerencia.marca : ''}`}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BUSCADOR-PRO: aviso de que lo que se ve es lo MAS PARECIDO, no lo que se escribio.
            Sin esto, el vendedor podria creer que el producto que busco es el que tiene delante. */}
        {/* El aviso solo cuando de verdad NO aparecio lo que se escribio: o porque no habia
            NADA exacto, o porque se entendio otra marca. Si el producto buscado SI salio y solo
            se anadieron parecidos debajo, decir "no lo encontramos" seria mentir. */}
        {!cargando && conTolerancia && productos.length > 0 && (marcaEntendida || totalExacto === 0) && (
          <div style={{
            margin: '0 16px 12px', padding: '10px 14px', borderRadius: 10,
            background: '#fff8e6', border: '1px solid #f0dca8', color: '#7a5b17', fontSize: 12,
          }}>
            {marcaEntendida
              ? <>No encontramos <b>{busquedaDeb}</b>. Te mostramos <b>{marcaEntendida}</b>, que es lo más parecido.</>
              : <>No encontramos <b>{busquedaDeb}</b> exacto. Esto es lo más parecido del catálogo.</>}
          </div>
        )}

        {/* BUSCADOR-PRO: cuando lo primero es el modelo EXACTO que se escribio, debajo van sus
            variantes. Buscar "DECO-BE22-3PACK" y ver solo ese producto es correcto e inutil: el
            vendedor casi siempre quiere comparar el de 2 y el de 3 piezas. */}
        {!cargando && familia.length > 0 && (
          <div style={{ margin: '0 16px 16px' }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              De la misma familia
              <span style={{ fontWeight: 500, fontSize: 11, color: COLOR_GRIS_CLARO }}>
                {familia.length} {familia.length === 1 ? 'variante' : 'variantes'}
              </span>
            </div>
            {/* El carril lleva ALTO PROPIO, no crece con su contenido. Sin eso, un carril con
                scroll horizontal puede empujar su barra fuera de la pantalla — es la regla que
                vigila scripts/portero-modales.js, y viene del bug en que no se alcanzaba el
                boton Guardar desde el celular. Las tarjetas son de alto fijo, asi que 150 px las
                contiene con holgura y deja sitio a la barra. */}
            <div style={{
              display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6,
              height: 150, alignItems: 'stretch',
              WebkitOverflowScrolling: 'touch',
            }}>
              {familia.map(p => (
                <button
                  key={p.id_unico}
                  onClick={() => setBusqueda(p.modelo)}
                  style={{
                    flex: '0 0 auto', width: 190, textAlign: 'left', cursor: 'pointer',
                    background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
                    padding: 10, fontFamily: 'inherit', minHeight: 44,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_PRINCIPAL, marginBottom: 2 }}>
                    {p.modelo}
                  </div>
                  <div style={{
                    fontSize: 11, color: COLOR_GRIS, lineHeight: 1.3, height: 28,
                    overflow: 'hidden',
                  }}>{p.nombre}</div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: COLOR_OSCURO }}>
                    {p.precio_efectivo_mxn != null
                      ? `$${Number(p.precio_efectivo_mxn).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`
                      : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: p.stock > 0 ? '#2e7d32' : COLOR_GRIS_CLARO }}>
                    {p.stock > 0 ? `${p.stock} en stock` : 'sin stock'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Grid */}
        {cargando && productos.length === 0 ? (
          <SkeletonGrid isMobile={isMobile} />
        ) : (
          <div style={gridStyle}>
            {productos.map(p => (
              <TarjetaProducto
                key={p.id_unico}
                producto={p}
                onAgregarCarrito={handleAgregarCarrito}
                onAgregarLista={handleAgregarLista}
                onAgregarColeccion={handleAgregarColeccion}
                onCompartir={handleCompartir}
                onDuplicarMisProductos={handleDuplicarMisProductos}
                onRefrescarSyscom={handleRefrescarSyscom}
                modoCotizacion={!!modoAgregar}
                modoInventario={modoInventario}
                onHojaCompras={modoInventario && veCompras ? irAHojaCompras : undefined}
              />
            ))}
          </div>
        )}

        {/* Sentinel para scroll infinito */}
        {hayMas && !cargando && (
          <div ref={sentinelRef} style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            {cargandoMas ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} />
                Cargando más productos…
              </span>
            ) : 'Desliza para ver más'}
          </div>
        )}

        {!hayMas && productos.length > 0 && !cargando && (
          <div style={{
            padding: '24px 16px 100px', textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <PartyPopper size={14} strokeWidth={2} /> No hay más productos en esta búsqueda.
          </div>
        )}

        {/* Skeleton extra mientras carga "más" */}
        {cargandoMas && (
          <div style={gridStyle}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{
                background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
                padding: 12, height: 320,
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div className="skeleton" style={{ height: '60%', borderRadius: 8 }} />
                <div className="skeleton" style={{ height: 12, width: '40%', borderRadius: 4 }} />
                <div className="skeleton" style={{ height: 14, borderRadius: 4 }} />
                <div className="skeleton" style={{ height: 22, width: '50%', borderRadius: 4, marginTop: 'auto' }} />
              </div>
            ))}
          </div>
        )}
          </div>
        </div>

      {/* Menú overlay mobile (pila de pantallas) */}
      {isMobile && (
        <MenuMobile
          open={menuAbierto}
          onCerrar={() => setMenuAbierto(false)}
          onSeleccionar={handleSeleccionarCategoria}
        />
      )}

      {/* Mega panel desktop (3 columnas con hover) */}
      {!isMobile && (
        <MegaPanelDesktop
          open={menuAbierto}
          onCerrar={() => setMenuAbierto(false)}
          onSeleccionar={handleSeleccionarCategoria}
        />
      )}

      {/* F3.B: el carrito se OCULTA mientras se agrega a una cotizacion (modo independiente). */}
      {!modoAgregar && <CarritoFlotante triggerOculto />}

      {modalLista && (
        <AgregarAListaModal
          item={modalLista}
          onCerrar={() => setModalLista(null)}
          onGuardado={() => {
            setModalLista(null);
            mostrarToast('✅ Agregado a lista pactada');
          }}
        />
      )}

      {modalColeccion && (
        <AgregarAColeccionModal
          producto={modalColeccion}
          onCerrar={() => setModalColeccion(null)}
          onAgregado={(count, nombres) => {
            setModalColeccion(null);
            const destino = nombres && nombres.length === 1 ? nombres[0] : `${count} colecciones`;
            mostrarToast('✅ Agregado a ' + destino);
          }}
        />
      )}

      {modalCompartir && (
        <ModalCompartir
          producto={modalCompartir}
          onCerrar={() => setModalCompartir(null)}
        />
      )}

      {/* DUPLICAR-MIS-PRODUCTOS: mismo modal de CatalogoAdmin, pre-llenado con Syscom
          (alta) o con el producto propio existente (edicion si "ya lo tienes"). */}
      {modalDuplicar && (
        <ModalProducto
          producto={modalDuplicar.producto}
          onGuardar={(payload) => guardarDuplicado(payload, modalDuplicar.idExistente)}
          onCerrar={() => setModalDuplicar(null)}
          guardando={guardandoDuplicar}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: tokens.primaryActive, color: '#fff', padding: '12px 18px', borderRadius: 12,
          fontSize: 14, fontWeight: 600, zIndex: 1500, fontFamily: PP, maxWidth: '90%',
          boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
          animation: 'toastIn 0.22s ease',
        }}>{toast}</div>
      )}

      <style>{`
        .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        /* UI-1.C Mejora 3: press feedback sutil en todos los botones (estilo Stripe/Linear) */
        button:active:not(:disabled),
        [role="button"]:active:not(:disabled) {
          transform: scale(0.97);
          transition: transform 80ms ease-out;
        }
        /* UI-1.C Mejora 4: foco visible solo por teclado. !important para vencer los outline:none inline */
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible,
        button:focus-visible {
          outline: 2px solid ${COLOR_OSCURO} !important;
          outline-offset: 2px;
        }
        /* UI-1.E: toast entra con fade + slide suave (preserva el translateX(-50%) de centrado) */
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        /* UI-1.E: respetar prefers-reduced-motion (neutraliza animaciones/transiciones sin tocar layout) */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </div>
  );
}

function SkeletonGrid({ isMobile }) {
  return (
    <div style={{
      display: 'grid', gap: isMobile ? 10 : 14, padding: isMobile ? 10 : 16,
      gridTemplateColumns: isMobile
        ? 'repeat(2, minmax(0, 1fr))'
        : 'repeat(5, minmax(0, 1fr))',
    }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} style={{
          background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
          padding: 12, height: 300,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div className="skeleton" style={{ height: '60%', borderRadius: 8 }} />
          <div className="skeleton" style={{ height: 12, borderRadius: 4, width: '40%' }} />
          <div className="skeleton" style={{ height: 16, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 16, borderRadius: 4, width: '70%' }} />
          <div className="skeleton" style={{ height: 22, borderRadius: 4, width: '50%', marginTop: 'auto' }} />
          <div className="skeleton" style={{ height: 40, borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

function BarraOrden({ isMobile, total, orden, onCambiar }) {
  const opciones = [
    { v: 'precio_asc',  m: 'Precio menor', d: 'Precio: menor a mayor' },
    { v: 'precio_desc', m: 'Precio mayor', d: 'Precio: mayor a menor' },
    { v: 'stock_desc',  m: 'Más stock',    d: 'Más stock disponible' },
  ];
  return (
    <div style={{
      padding: isMobile ? '8px 12px' : '10px 16px',
      background: isMobile ? '#fff' : tokens.surfaceSubtle,
      borderBottom: `1px solid ${COLOR_BORDE}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
      margin: isMobile ? 0 : '0 16px 12px',
      borderRadius: isMobile ? 0 : 8,
    }}>
      <span style={{ fontSize: isMobile ? 13 : 14, color: COLOR_GRIS, fontWeight: 600 }}>
        {total} producto{total === 1 ? '' : 's'}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: isMobile ? 12 : 13, color: COLOR_GRIS_CLARO }}>
          {isMobile ? 'Ordenar:' : 'Ordenar por:'}
        </span>
        <select
          value={orden}
          onChange={(e) => onCambiar(e.target.value)}
          style={{
            padding: isMobile ? '6px 10px' : '8px 12px',
            fontSize: isMobile ? 13 : 14,
            fontWeight: 600,
            color: COLOR_OSCURO,
            background: '#fff',
            border: `1px solid ${COLOR_BORDE}`,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: PP,
            minHeight: isMobile ? 36 : 40,
            minWidth: isMobile ? 0 : 200,
            touchAction: 'manipulation',
          }}
        >
          {opciones.map(o => (
            <option key={o.v} value={o.v}>{isMobile ? o.m : o.d}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
